import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LandingToolBar from '../Landing/LangingToolBar';
import MUITypography from '../../Components/MUITypography';
import { VM_Colors } from '../../Styles/VMStyles';
import { toTitleCase } from '../../Util/UtilHelper';

const greenIcon = <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 18 }} />;
const redIcon = <CancelIcon sx={{ color: '#d32f2f', fontSize: 18 }} />;

const features = [
  {
    name: 'Users',
    basic: { icon: greenIcon, text: 'Admin (Full Access, Lifetime)' },
    premium: {
      icon: greenIcon,
      text:
        'Unlimited Users with Roles (Admin, Manager, Executive, Receptionist, Security Guards)',
    },
  },
  {
    name: 'Venue Availability Check',
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Scheduling Appointments',
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: "Scheduling Follow-up’s",
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available for All Role' },
  },
  {
    name: 'Enquiry Management',
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Event Vendors',
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Dashboard',
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Revenue Checker',
    basic: { icon: greenIcon, text: 'Available for Admin' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Change of Logo',
    basic: { icon: greenIcon, text: 'Not Customisable' },
    premium: { icon: greenIcon, text: 'Customisable Branding' },
  },
  {
    name: 'Invoice',
    basic: { icon: greenIcon, text: 'Default Watermark' },
    premium: { icon: greenIcon, text: 'Custom Watermark (Your Branding)' },
  },
  {
    name: 'Roles',
    basic: { icon: redIcon, text: 'Not Available (Only Admin)' },
    premium: { icon: greenIcon, text: 'Role-Based Access & Permissions' },
  },
  {
    name: 'Task Manager',
    basic: { icon: redIcon, text: 'Not Available' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Checklist Manager',
    basic: { icon: redIcon, text: 'Not Available' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Feedback Management',
    basic: { icon: redIcon, text: 'Not Available' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Visitor Management',
    basic: { icon: redIcon, text: 'Not Available' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Adding Event Photos',
    basic: { icon: redIcon, text: 'Not Available' },
    premium: { icon: greenIcon, text: 'Available as per Role' },
  },
  {
    name: 'Download Records',
    basic: { icon: redIcon, text: 'Not Available' },
    premium: {
      icon: greenIcon,
      text: 'Unlimited Download & Export Options',
    },
  },
  // {
  //   name: 'Pricing',
  //   basic: {
  //     icon: greenIcon,
  //     text: 'Free Trial for 1 Month, Then Limited Access (Admin Only)',
  //   },
  //   premium: { icon: greenIcon, text: 'Yearly Subscription' },
  // },
];

const PricingTable = () => {
  return (
    <Box sx={{backgroundColor:VM_Colors.white1,pb:10}}>
      <LandingToolBar />

      <Container maxWidth="lg" sx={{ mt: 6, mb: 8 }}>
        {/* HEADER */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
          <MUITypography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
            }}
          >
            Simple Pricing, Powerful Features
          </MUITypography>

          <MUITypography
            variant="h4"
            color="text.secondary"
            sx={{
              maxWidth: 700,
              mx: 'auto',
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
            }}
          >
           {toTitleCase(" Discover the ideal plan to fuel your business growth. Our pricing  options are carefully crafted to cater to business.")}
          </MUITypography>
        </Box>

        {/* TABLE */}
        <TableContainer
          component={Paper}
          elevation={1}
          sx={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#cfd8dc',
              borderRadius: 4,
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: { xs: 40, sm: 60 }, fontWeight: 600 }}>
                  S.No
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: 600,
                    pr: { xs: 3, sm: 5, md: 6 },
                    minWidth: { xs: 160, sm: 220, md: 260 },
                  }}
                >
                  Features
                </TableCell>

                <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>
                  Free Version
                </TableCell>

                <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>
                  Premium Version
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {features.map((row, index) => (
                <TableRow key={row.name} hover>
                  <TableCell
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    }}
                  >
                    {index + 1}
                  </TableCell>

                  <TableCell
                    sx={{
                      pr: { xs: 3, sm: 5, md: 6 },
                      minWidth: { xs: 160, sm: 220, md: 260 },
                    }}
                  >
                    <Typography
                      fontWeight={500}
                      sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ minWidth: 200 }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      {row.basic.icon}
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                      >
                        {row.basic.text}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ minWidth: 220 }}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      {row.premium.icon}
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
                      >
                        {row.premium.text}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Box>
  );
};

export default PricingTable;